// Utility functions for permission checking

import axios from "const/axios";
import { P } from "const/permissions";

let refreshPermissionsInFlight = null;

export const PERMISSIONS_REFRESHED_EVENT = "kothar:permissions-refreshed";

export const PLATFORM_SCOPED_KEYS = [
  P.CONSULTANCY.READ,
  P.CONSULTANCY.CREATE,
  P.CONSULTANCY.UPDATE,
  P.CONSULTANCY.MEMBERSHIP_ASSIGN_SUPERADMIN,
  P.PLATFORM.PERMISSION_MANAGE,
];

export const isPlatformScopedKey = (key) => {
  if (!key) return false;
  return PLATFORM_SCOPED_KEYS.includes(String(key).toLowerCase());
};

export const getUserPermissions = () => {
  try {
    const userDetail = JSON.parse(localStorage.getItem("userDetail"));
    const permittedModules = JSON.parse(localStorage.getItem("permittedModules")) || [];
    const platformPermissions =
      userDetail?.platformPermissions ||
      JSON.parse(localStorage.getItem("platformPermissions") || "[]");
    return { userDetail, permittedModules, platformPermissions };
  } catch (error) {
    console.error("Error getting user permissions:", error);
    return { userDetail: null, permittedModules: [], platformPermissions: [] };
  }
};

export const isPlatformAdmin = () => {
  const { userDetail } = getUserPermissions();
  return Boolean(userDetail?.platformAdmin);
};

export const isOrgSuperAdmin = () => {
  const { userDetail } = getUserPermissions();
  const orgRole = userDetail?.orgRole || userDetail?.type;
  return orgRole === "SUPER_ADMIN";
};

export const hasPlatformPermissionKey = (requiredKey) => {
  if (!requiredKey) return true;
  if (isPlatformAdmin()) return true;
  const { platformPermissions } = getUserPermissions();
  const key = String(requiredKey).toLowerCase();
  return (platformPermissions || []).map((p) => String(p).toLowerCase()).includes(key);
};

export const canAccessPlatformOrganisations = () => {
  return isPlatformAdmin() || hasPlatformPermissionKey(P.CONSULTANCY.READ);
};

export const canCreatePlatformOrganisation = () => {
  return isPlatformAdmin() || hasPlatformPermissionKey(P.CONSULTANCY.CREATE);
};

export const canManagePlatformOrganisations = () => {
  return (
    isPlatformAdmin() ||
    hasPlatformPermissionKey(P.CONSULTANCY.UPDATE) ||
    hasPlatformPermissionKey(P.CONSULTANCY.CREATE)
  );
};

/** Can assign/promote a SUPER_ADMIN membership in any organisation, without being a member. */
export const canAssignOrgSuperAdmin = () => {
  return isPlatformAdmin() || hasPlatformPermissionKey(P.CONSULTANCY.MEMBERSHIP_ASSIGN_SUPERADMIN);
};

/** Can grant/revoke platform-level permissions for other users (the platform-superadmin capability). */
export const canManagePlatformPermissionGrants = () => {
  return isPlatformAdmin() || hasPlatformPermissionKey(P.PLATFORM.PERMISSION_MANAGE);
};

export const hasPermission = (requiredPermission) => {
  return hasPermissionKey(requiredPermission);
};

/**
 * Permission check for module:resource:action keys.
 * Platform-scoped keys (consultancy:org:*) never inherit org SUPER_ADMIN bypass.
 */
export const hasPermissionKey = (requiredKey) => {
  if (!requiredKey) {
    return true;
  }
  if (Array.isArray(requiredKey)) {
    return requiredKey.some((key) => hasPermissionKey(key));
  }
  if (isPlatformScopedKey(requiredKey)) {
    return hasPlatformPermissionKey(requiredKey);
  }
  if (isPlatformAdmin() || isOrgSuperAdmin()) {
    return true;
  }

  const { permittedModules } = getUserPermissions();
  const owned = (permittedModules || []).map((p) => String(p).toLowerCase());
  const key = String(requiredKey).toLowerCase();
  return owned.includes(key);
};

export const refreshPermissions = async (token) => {
  const authToken = token || localStorage.getItem("token");
  if (!authToken) {
    return null;
  }

  if (refreshPermissionsInFlight) {
    return refreshPermissionsInFlight;
  }

  refreshPermissionsInFlight = (async () => {
    try {
      const res = await axios.get("users/me/permissions", {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const payload = res?.data?.data || {};
      const perms = payload.permissions || [];
      const platformPerms = payload.platformPermissions || [];

      localStorage.setItem("permittedModules", JSON.stringify(perms));
      localStorage.setItem("platformPermissions", JSON.stringify(platformPerms));

      const userDetail = JSON.parse(localStorage.getItem("userDetail") || "null") || {};
      localStorage.setItem(
        "userDetail",
        JSON.stringify({
          ...userDetail,
          platformPermissions: platformPerms,
          platformAdmin: payload.platformAdmin ?? userDetail.platformAdmin ?? false,
          orgRole: payload.orgRole ?? userDetail.orgRole,
        })
      );

      window.dispatchEvent(new Event(PERMISSIONS_REFRESHED_EVENT));
      return perms;
    } catch (error) {
      console.error("Failed to refresh permissions:", error);
      return null;
    } finally {
      refreshPermissionsInFlight = null;
    }
  })();

  return refreshPermissionsInFlight;
};

export const checkMultiplePermissions = (requiredPermissions) => {
  if (!requiredPermissions || !Array.isArray(requiredPermissions)) {
    return false;
  }
  return requiredPermissions.some((permission) => hasPermissionKey(permission));
};

export const getUserType = () => {
  try {
    const userDetail = JSON.parse(localStorage.getItem("userDetail"));
    return userDetail?.orgRole || userDetail?.type || null;
  } catch (error) {
    console.error("Error getting user type:", error);
    return null;
  }
};

export const isSuperAdmin = () => {
  return isOrgSuperAdmin();
};

export const isAdmin = () => {
  const userType = getUserType();
  return userType === "ADMIN" || userType === "SUPER_ADMIN";
};

export const isAccount = () => {
  return getUserType() === "ACCOUNT";
};

export const canVerifyPayments = () => {
  const userType = getUserType();
  return userType === "ACCOUNT" || userType === "SUPER_ADMIN" || isPlatformAdmin();
};

/** Mirrors TermCreditAdjustmentController: anyone who may record a term payment may ask for a move. */
export const canRequestCreditAdjustment = () =>
  hasPermissionKey(["accounts:account:update", "accounts:account:client-manage"]);

/** Applying a credit move without verification takes the same bar as verifying a payment. */
export const canApplyCreditAdjustmentNow = () =>
  (isSuperAdmin() || isAccount()) && hasPermissionKey("accounts:payment:verify");

/**
 * Commission, bonus and profit/loss are the agency's money, not the client's — mirrors the
 * backend's AccountFinancialAccess, which blanks those fields for anyone these return false for.
 * Gating the UI on the same two keys keeps the screen honest about what the API actually sent.
 */
export const canViewAgencyFinancials = () =>
  hasPermissionKey("accounts:account:read") || hasPermissionKey("admissions:commission:read");

export const canAdjustAgencyFinancials = () =>
  hasPermissionKey("accounts:account:update") || hasPermissionKey("admissions:commission:update");
